import {ActionTree} from 'vuex'
import {MediaState} from '@/modules/media/store'
import {RootState} from '@/store/types'
import * as api from '@/modules/media/api'
import {MediaListFilters} from '@/modules/media/api'
import {MutationTypes} from '@/modules/media/store/mutations'
import {MediaObject, MediaUpload, MediaUploadState, QueueState, ThumbnailSize} from '@/modules/media/types'
import {Colors, notify} from '@/modules/core/notification'
import {compress, thumbnails} from '@/modules/media/utils/compress'

export enum ActionTypes {
    LoadMediaObjects = 'objects/load',
    LoadLibrary = 'library/load',
    UpdateMediaObject = 'object/update',
    DeleteMediaObject = 'object/delete',
    DeleteObjectSelection = 'object/selection/delete',
    LoadMediaObject = 'loadMediaObject',

    // Acciones para el administrador de subidas
    PushFiles = 'pushFiles',
    ProcessQueue = 'processQueue',
    LoadUploadConstrains = 'loadUploadConstrains',
    SaveMediaObjectFile = 'saveMediaObjectFile',
}

export interface UploadProcess {
    file: File,
    response: string | object
}

const actions: ActionTree<MediaState, RootState> = {

    /**
     * Obtiene de la api los objetos multimedia registrados en al aplicación y
     * los carga en el estado
     */
    [ActionTypes.LoadMediaObjects]: async ({commit}, filters = new MediaListFilters()) => {
        const objects = await api.listMedia(filters)
        commit(MutationTypes.SetMediaObjects, objects)
    },

    /**
     * Carga la librería multimedia
     */
    [ActionTypes.LoadLibrary]: async ({dispatch, commit}) => {
        commit('showLoadingScreen', {message: 'Cargando librería multimedia'}, {root: true})
        await dispatch(ActionTypes.LoadMediaObjects)

        commit('hideLoadingScreen', {}, {root: true})
    },

    /**
     * Actualiza un objeto multimedia
     */
    [ActionTypes.UpdateMediaObject]: async ({commit}, media: MediaObject) => {
        const updated = await api.updateMediaObject(media)
        commit(MutationTypes.UpdateMediaObject, media)

        // Muestra una notificación al usuario indicando que el archivo se ha actualizado
        notify('Archivo actualizado correctamente')

        return updated
    },
    /**
     * Elimina un objeto multimedia
     */
    [ActionTypes.DeleteMediaObject]: async ({commit}, payload: { media: MediaObject }) => {
        try {
            const ok = await api.deleteMediaObject(payload.media)
            if (!ok) {
                return
            }

            commit(MutationTypes.DeleteMediaObject, payload.media.id)
            notify('Archivo eliminado correctamente')
        } catch (e) {
            notify(e.message, Colors.Error)
        }
    },

    /**
     * Elimina los objetos multimedia seleccionados
     */
    [ActionTypes.DeleteObjectSelection]: async ({state, commit}) => {
        commit(MutationTypes.SetDeletingSelection, true)
        try {
            const result = await api.deleteMediaList(state.selectedObjects)

            result.removed.forEach(id => commit(MutationTypes.DeleteMediaObject, id))

            commit('showNotification', {message: 'Se ha eliminado la selección', color: 'success'}, {root: true})
        } catch (e) {
            notify(`Error: ${e.message}`, Colors.Error)
        }

        // Terminar proceso, cerrar modal de confirmación y restablecer el estado
        commit(MutationTypes.SetDeletingSelection, false)
        commit(MutationTypes.SetObjectSelection, [])
        commit(MutationTypes.ShowDeleteSelectionConfirm, false)
    },

    /**
     * Inserta una lista de ficheros en la cola actual de subida
     *
     * @param commit
     * @param state
     * @param files
     */
    [ActionTypes.PushFiles]: async ({commit, state}, files: FileList) => {
        const validTypes = state.validFileTypes
        const result = new PushFilesResult()

        // La cola se archivos actual ya ha sido procesada, será restablecida
        if (state.queueState === QueueState.Terminated) {
            commit(MutationTypes.ClearUploadQueue)
        }

        for (const file of Array.from(files)) {
            // El tipo del archivo que se intenta añadir a la cola no es válido
            if (!validTypes.includes(file.type)) {
                result.rejected.push({
                    file, reason: RejectedFileReason.InvalidType,
                })

                continue
            }

            // El archivo que se intenta añadir a la cola excede el tamaño máximo permitido
            if (file.size > state.maxFileSize) {
                result.rejected.push({
                    file, reason: RejectedFileReason.InvalidSize,
                })

                continue
            }

            // El fichero ya ha sido insertado en la cola
            const alreadyExists = state.uploadQueue.find(upload => upload.file!.name === file.name)
            if (alreadyExists) {
                result.rejected.push({
                    file, reason: RejectedFileReason.AlreadyPushed,
                })

                continue
            }

            const upload = new MediaUpload(file)
            // Si el archivo de subida es una imagen se genera una miniatura
            if (upload.isImage) {
                const thumbnail = await compress(file, {
                    width: 200,
                    quality: 0.8,
                    mimeType: 'image/jpeg',
                })

                upload.thumbnails.set(ThumbnailSize.Small, thumbnail)
                upload.thumbnail = URL.createObjectURL(thumbnail)
            }

            commit(MutationTypes.PushToUploadQueue, upload)
        }

        return result
    },

    /**
     * Procesar cola
     *
     * Sube todos los archivos al servidor
     */
    [ActionTypes.ProcessQueue]: async ({commit, state}) => {
        commit(MutationTypes.SetQueueState, QueueState.Processing)
        const queue = state.uploadQueue

        // Función de ayuda para cambiar el estado de una subida
        const updateState = (upload: MediaUpload | string, state: MediaUploadState) =>
            commit(MutationTypes.SetMediaUploadState, {
                id: upload instanceof MediaUpload ? upload.id : upload,
                state,
        })

        for (const upload of queue) {
            // Si el archivo de subida es una imagen será comprimida y se generará
            // miniaturas el servidor
            if (upload.isImage) {
                updateState(upload, MediaUploadState.Optimizing)
                upload.optimizedFile = await compress(upload.file!, {
                    quality: 0.6,
                    strict: true,
                })

                // Genera un conjunto de miniaturas de la imagen para ser registradas en el servidor
                const generated = await thumbnails(upload.file!, state.thumbnailSizes)
                generated.forEach((thumbnail, size) => {
                    upload.thumbnails.set(size, thumbnail)
                })
            }
        }

        const { requests, master } = api.upload(queue.map(upload => {
            updateState(upload, MediaUploadState.Transferring)
            return {
                upload, customThumbnails: true,
            }
        }))

        requests.forEach((promise, uploadId) => {
            promise
                .then((media: MediaObject) => {
                    updateState(uploadId, MediaUploadState.Success)
                    // Actualiza el objeto MediaUpload y añade el objeto multimedia asociado con
                    // la subida.
                    commit(MutationTypes.UpdateMediaUploadField, {
                        id: uploadId,
                        field: 'media',
                        value: media,
                    })
                    commit(MutationTypes.AddMediaObject, media)
                })
                .catch(() => updateState(uploadId, MediaUploadState.Failed))
        })


        const terminated = () => commit(MutationTypes.SetQueueState, QueueState.Terminated)
        return master.then(terminated).catch(terminated)
    },
    /**
     * Solicita al servidor lo límites para la subida de ficheros
     *
     * @param state
     * @param commit
     */
    [ActionTypes.LoadUploadConstrains]: async ({state, commit}) => {
        if (state.uploadConstrainsFetched) {
            return
        }

        const constrains = await api.getUploadConstrains()
        commit(MutationTypes.SetAllowedTypes, constrains.allowedTypes)
        commit(MutationTypes.SetUploadMaxSize, constrains.maxUploadSize)
        commit(MutationTypes.SetThumbnailSizes, constrains.thumbnailSizes)
        commit(MutationTypes.ToggleUploadConstrainsFetched, true)
    },

    /**
     * Actualiza el fichero asociado al objeto multimedia actualmente en edición
     *
     * @param state
     * @param commit
     * @param payload
     */
    [ActionTypes.SaveMediaObjectFile]: async ({state, commit}, payload: {file: Blob }) => {
        // Solicita al servidor que reemplace el archivo por el nuevo
        const updatedMedia = await api.updateMediaObjectFile(
            state.currentMedia.id,
            payload.file,
        )

        commit(MutationTypes.AddMediaObject, updatedMedia)
        commit(MutationTypes.SetCurrentMedia, updatedMedia)
    },

    /**
     * Carga un archivo multimedia y la guarda en el estado de la aplicación
     * como archivo actualmente seleccionado por el usuario
     *
     * @param state
     * @param commit
     * @param id
     */
    [ActionTypes.LoadMediaObject]: async ({state, commit}, id: number) => {
        let media = state.objects.find(media => media.id === id)
        // Si el archivo no se encuentra actualmente cargando en el estado de la aplicación
        // se solicitar al servidor
        if (!media) {
            media = await api.getMedia(id)
        }

        commit(MutationTypes.SetCurrentMedia, media)
    },
}

export enum RejectedFileReason {
    InvalidType = 'INVALID_FILE_TYPE',
    InvalidSize = 'INVALID_FILE_SIZE',
    AlreadyPushed = 'FILE_ALREADY_PUSHED',
}

/**
 * Resultado de ejecutar la acción de añadir ficheros a la cola de subida
 *
 * @author Alejandro G. Riaño <agutierrez@softcontrol.es>
 */
export class PushFilesResult {
    /**
     * Lista de archivos aceptados, los cuales han sido insertados en la cola
     * de subida
     */
    public accepted: File[] = []

    /**
     * Lista de archivos rechazados que no han sido insertados en la cola
     * de subida.
     */
    public rejected: Array<{file: File, reason: RejectedFileReason }> = []

    /**
     * Indica si el resultado tiene archivos rechazados
     */
    public get hasRejected (): boolean {
        return !!this.rejected.length
    }
}

export default actions
